/**
 * AudioRecorder Component
 * Provides audio recording interface with visual feedback and controls
 * Integrates with useAudioRecorder hook for recording functionality
 *
 * Accessibility Features:
 * - Full keyboard navigation (Space, Enter, Escape)
 * - ARIA labels and live regions
 * - Screen reader announcements
 * - Focus management
 */

import { ArrowUpward, DeleteOutline } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAudioRecorder } from '../../hooks/use-audio-recorder';
import { useAudioVisualization } from '../../hooks/use-audio-visualization';
import type { AudioRecordingData } from '../../models/audio-recorder.types';
import { RecordingState } from '../../models/audio-recorder.types';
import { downsample } from '../../utils/audio-recorder-downsample';
import { requestMicrophonePermission } from '../../utils/audio-recorder-permissions';
import { AudioWaveformCanvas } from './audio-waveform-canvas';

/**
 * Component Props Interface
 */
export interface AudioRecorderProps {
  /** Callback when send button is clicked */
  onSend?: (data: AudioRecordingData) => void;
  /** Callback when recording is cancelled */
  onCancel?: () => void;
  /** Callback for recording errors */
  onError?: (error: Error) => void;
  /** Maximum recording duration in seconds (default: 300 = 5 minutes) */
  maxDuration?: number;
  /** Minimum recording duration in seconds (default: 1) */
  minDuration?: number;
  /** Disable the recorder */
  disabled?: boolean;
  /** Custom className for styling */
  className?: string;
}

/**
 * Format time in MM:SS format
 */
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

/**
 * AudioRecorder Component
 * Main recording interface with Material-UI components and Framer Motion animations
 */
export const AudioRecorder = ({
  onSend,
  onCancel,
  onError,
  maxDuration = 180,
  minDuration = 1,
  disabled = false,
  className
}: AudioRecorderProps) => {
  const { t } = useTranslation();
  // Use audio recorder hook
  const { state, startRecording, stopRecording, cancelRecording } = useAudioRecorder();

  // Extract state properties
  const { recordingState, duration, error, isSupported } = state;

  // Check if recording is active
  const isRecording = useMemo(() => recordingState === RecordingState.RECORDING, [recordingState]);

  // Check if requesting permission
  const isRequesting = useMemo(() => recordingState === RecordingState.REQUESTING, [recordingState]);

  // Setup audio analyser and visualization
  const analyserRef = useRef<AnalyserNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  // Use audio visualization hook
  const { canvasRef, waveformSamplesRef } = useAudioVisualization({
    analyserNode: analyserRef.current,
    isRecording,
    barColor: undefined, // Will use theme primary color
    backgroundColor: undefined // Will use transparent
  });

  // Ref for live region announcements
  const liveRegionRef = useRef<HTMLDivElement>(null);

  // Announce to screen readers
  const announce = useCallback((message: string) => {
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = message;
    }
  }, []);

  // Check if can send (meets minimum duration)
  const canSend = useMemo(() => duration >= minDuration, [duration, minDuration]);

  // Handle send recording
  const handleSend = useCallback(async () => {
    if (!canSend) return;

    try {
      const data = await stopRecording();
      if (data) {
        // Downsample captured waveform to exactly 100 points
        const waveformSamples = waveformSamplesRef.current;
        const downsampledWaveform = waveformSamples.length > 0 ? downsample(new Uint8Array(waveformSamples), 100) : [];

        // Attach downsampled waveform to recording data
        const dataWithWaveform = {
          ...data,
          waveformData: downsampledWaveform
        };

        onSend?.(dataWithWaveform);

        // Clear waveform samples
        waveformSamplesRef.current = [];
      }
    } catch (err) {
      onError?.(err as Error);
    }
  }, [canSend, stopRecording, onSend, onError]);

  // Handle cancel recording
  const handleCancel = useCallback(() => {
    cancelRecording();
    waveformSamplesRef.current = []; // Clear waveform samples
    onCancel?.();
  }, [cancelRecording, onCancel]);

  // Setup audio visualization
  const setupVisualization = useCallback((stream: MediaStream) => {
    mediaStreamRef.current = stream;
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);

    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    source.connect(analyser);

    analyserRef.current = analyser;
  }, []);

  // Auto-start recording on mount
  useEffect(() => {
    const initRecording = async () => {
      try {
        // Reset waveform samples
        waveformSamplesRef.current = [];

        // Get stream first for visualization
        const stream = await requestMicrophonePermission();
        setupVisualization(stream);

        // Then start recording
        await startRecording();
        announce(t('messages.audioRecorder.announceStart'));
      } catch (err) {
        onError?.(err as Error);
      }
    };

    initRecording();

    // Cleanup
    return () => {
      // Stop visualization
      // Cleanup media stream
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [setupVisualization, startRecording, announce, onError]);

  // Auto-stop when max duration is reached
  useEffect(() => {
    if (isRecording && duration >= maxDuration) {
      handleSend();
    }
  }, [isRecording, duration, maxDuration, handleSend]);

  // Handle error state
  useEffect(() => {
    if (error) {
      onError?.(new Error(error.message));
    }
  }, [error, onError]);

  // Keyboard event handler
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const { key } = event;

      if (key === 'Enter' && canSend) {
        event.preventDefault();
        handleSend();
        announce(t('messages.audioRecorder.announceSend'));
      } else if (key === 'Escape') {
        event.preventDefault();
        handleCancel();
        announce(t('messages.audioRecorder.announceCancel'));
      }
    },
    [canSend, handleSend, handleCancel, announce, t]
  );

  // Show not supported message
  if (!isSupported) {
    return (
      <Box className={className} role='alert' aria-live='polite'>
        <Typography variant='body2' color='error'>
          {t('messages.audioRecorder.errors.browserNotSupported')}
        </Typography>
      </Box>
    );
  }

  // Show loading state
  if (isRequesting) {
    return (
      <Box className={`${className} mex:flex mex:items-center mex:justify-center mex:p-4`}>
        <CircularProgress size={24} />
        <Typography variant='body2' sx={{ ml: 2 }}>
          {t('messages.audioRecorder.requestingPermission')}
        </Typography>
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.8, y: 20 }}
      transition={{
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1]
      }}
    >
      <Box
        className={className}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        aria-label={t('messages.audioRecorder.controls')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          borderRadius: 2,
          bgcolor: 'background.paper',
          boxShadow: 2,
          outline: 'none',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2
          }
        }}
      >
        {/* Cancel Button */}
        <Tooltip title={t('messages.audioRecorder.cancelTooltip')}>
          <IconButton
            color='error'
            onClick={handleCancel}
            disabled={disabled}
            size='small'
            aria-label={t('messages.audioRecorder.cancelAria')}
            sx={{
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'text.primary',
                outlineOffset: 2
              }
            }}
          >
            <DeleteOutline />
          </IconButton>
        </Tooltip>

        {/* Waveform Visualization */}
        <Box sx={{ flex: 1, height: 40, position: 'relative', bgcolor: 'action.hover', borderRadius: 1 }}>
          <AudioWaveformCanvas
            ref={canvasRef}
            ariaLabel={t('messages.audioRecorder.waveformAria')}
            sx={{ width: '100%', height: '100%' }}
          />

          {/* Recording indicator dot */}
        </Box>

        {/* Timer */}
        <Typography
          variant='body2'
          component='time'
          aria-label={t('messages.audioRecorder.timerAria', { time: formatTime(duration) })}
          aria-live='polite'
          sx={{
            fontFamily: 'monospace',
            fontWeight: 'medium',
            minWidth: 50,
            color: duration >= maxDuration * 0.9 ? 'warning.main' : 'text.primary'
          }}
        >
          {formatTime(duration)}
        </Typography>

        {/* Send Button */}
        <Tooltip
          title={
            canSend
              ? t('messages.audioRecorder.sendTooltip')
              : t('messages.audioRecorder.minDuration', { duration: minDuration })
          }
        >
          <span>
            <IconButton
              color='primary'
              size='small'
              onClick={handleSend}
              disabled={disabled || !canSend}
              aria-label={t('messages.audioRecorder.sendAria')}
              className='mex:rounded-sm mex:bg-primary/20'
            >
              <ArrowUpward color='primary' />
            </IconButton>
          </span>
        </Tooltip>

        {/* Live region for screen reader announcements */}
        <Box
          ref={liveRegionRef}
          role='status'
          aria-live='polite'
          aria-atomic='true'
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            margin: -1,
            padding: 0,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0
          }}
        />
      </Box>
    </motion.div>
  );
};

export default AudioRecorder;
